import React, { useCallback, useEffect, useState } from 'react';
import { FiEdit2, FiEye, FiEyeOff, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import {
  clientPortalAdminService,
  type PostOrderRow,
} from '../../services/clientPortalAdminService';
import { useI18n } from '../../i18n';
import './PostOrdersModal.css';

interface Props {
  siteId: string;
  siteName: string;
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'duties', label: 'Duties' },
  { value: 'emergency', label: 'Emergency procedures' },
  { value: 'escalation', label: 'Escalation' },
  { value: 'access', label: 'Access control' },
  { value: 'reporting', label: 'Reporting' },
];

interface DraftState {
  id?: string;
  category: string;
  title: string;
  body: string;
  displayOrder: number;
  isClientVisible: boolean;
  effectiveFrom: string;
}

const emptyDraft = (): DraftState => ({
  category: 'general',
  title: '',
  body: '',
  displayOrder: 0,
  isClientVisible: true,
  effectiveFrom: '',
});

export default function PostOrdersModal({ siteId, siteName, onClose }: Props) {
  const { t } = useI18n();
  const [orders, setOrders] = useState<PostOrderRow[]>([]);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setOrders(await clientPortalAdminService.fetchPostOrders(siteId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    if (!draft.title.trim() || !draft.body.trim()) {
      setError(t('A post order needs a title and a body.'));
      return;
    }
    setIsBusy(true);
    setError('');
    try {
      await clientPortalAdminService.savePostOrder({
        id: draft.id,
        siteId,
        category: draft.category,
        title: draft.title.trim(),
        body: draft.body.trim(),
        displayOrder: draft.displayOrder,
        isClientVisible: draft.isClientVisible,
        effectiveFrom: draft.effectiveFrom || null,
      });
      setDraft(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const remove = async (postOrderId: string) => {
    setIsBusy(true);
    try {
      await clientPortalAdminService.deletePostOrder(postOrderId);
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const toggleVisibility = async (order: PostOrderRow) => {
    setIsBusy(true);
    try {
      await clientPortalAdminService.savePostOrder({
        id: order.id,
        siteId,
        category: order.category,
        title: order.title,
        body: order.body,
        displayOrder: order.display_order,
        isClientVisible: !order.is_client_visible,
        effectiveFrom: order.effective_from,
      });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal po-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('Post orders for {name}', { name: siteName })}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="po-modal__head">
          <div>
            <div className="po-modal__eyebrow">{t('Post orders')}</div>
            <h2>{siteName}</h2>
            <p>{t('The rules, duties and procedures guards follow here. Client-visible entries appear in the client portal.')}</p>
          </div>
          <button type="button" className="po-icon-btn" aria-label={t('Close')} onClick={onClose}>
            <FiX aria-hidden="true" />
          </button>
        </header>

        {error ? <div className="po-error">{error}</div> : null}

        <div className="po-modal__body">
          {isLoading ? (
            <div className="po-loading"><div className="spinner" /></div>
          ) : (
            <>
              {draft ? (
                <form className="po-form" onSubmit={save}>
                  <div className="po-form__row">
                    <label className="po-field">
                      <span>{t('Category')}</span>
                      <select
                        className="form__select"
                        value={draft.category}
                        onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                      >
                        {CATEGORIES.map((item) => (
                          <option key={item.value} value={item.value}>{t(item.label)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="po-field">
                      <span>{t('Order')}</span>
                      <input
                        type="number"
                        className="form__input"
                        value={draft.displayOrder}
                        onChange={(event) => setDraft({ ...draft, displayOrder: Number(event.target.value) })}
                      />
                    </label>
                    <label className="po-field">
                      <span>{t('Effective from')}</span>
                      <input
                        type="date"
                        className="form__input"
                        value={draft.effectiveFrom}
                        onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
                      />
                    </label>
                  </div>

                  <label className="po-field">
                    <span>{t('Title')}</span>
                    <input
                      type="text"
                      required
                      className="form__input"
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                      placeholder={t('e.g. Fire alarm activation')}
                    />
                  </label>

                  <label className="po-field">
                    <span>{t('Instructions')}</span>
                    <textarea
                      required
                      rows={6}
                      className="form__input"
                      value={draft.body}
                      onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                      placeholder={t('Step-by-step detail the officer should follow.')}
                    />
                  </label>

                  <label className="po-check">
                    <input
                      type="checkbox"
                      checked={draft.isClientVisible}
                      onChange={(event) => setDraft({ ...draft, isClientVisible: event.target.checked })}
                    />
                    <span>
                      <strong>{t('Visible in the client portal')}</strong>
                      <small>{t('Uncheck to keep this directive internal to your officers.')}</small>
                    </span>
                  </label>

                  <div className="po-form__actions">
                    <button type="submit" className="btn btn--primary" disabled={isBusy}>
                      {isBusy ? t('Saving…') : t('Save post order')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => { setDraft(null); setError(''); }}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary po-add"
                  onClick={() => setDraft(emptyDraft())}
                >
                  <FiPlus aria-hidden="true" /> {t('New post order')}
                </button>
              )}

              {orders.length === 0 && !draft ? (
                <div className="po-empty">{t('No post orders yet for this site.')}</div>
              ) : (
                <ul className="po-list">
                  {orders.map((order) => (
                    <li key={order.id} className={order.is_client_visible ? '' : 'po-item--internal'}>
                      <div className="po-item__main">
                        <div className="po-item__head">
                          <strong>{order.title}</strong>
                          <span className="po-cat">
                            {t(CATEGORIES.find((c) => c.value === order.category)?.label ?? order.category)}
                          </span>
                          {!order.is_client_visible ? (
                            <span className="po-cat po-cat--internal">{t('Internal only')}</span>
                          ) : null}
                        </div>
                        <p>{order.body}</p>
                      </div>
                      <div className="po-item__actions">
                        <button
                          type="button"
                          className="po-icon-btn"
                          aria-label={order.is_client_visible
                            ? t('Hide from client portal')
                            : t('Show in client portal')}
                          title={order.is_client_visible
                            ? t('Hide from client portal')
                            : t('Show in client portal')}
                          disabled={isBusy}
                          onClick={() => void toggleVisibility(order)}
                        >
                          {order.is_client_visible
                            ? <FiEye aria-hidden="true" />
                            : <FiEyeOff aria-hidden="true" />}
                        </button>
                        <button
                          type="button"
                          className="po-icon-btn"
                          aria-label={t('Edit {name}', { name: order.title })}
                          disabled={isBusy}
                          onClick={() => setDraft({
                            id: order.id,
                            category: order.category,
                            title: order.title,
                            body: order.body,
                            displayOrder: order.display_order,
                            isClientVisible: order.is_client_visible,
                            effectiveFrom: order.effective_from ?? '',
                          })}
                        >
                          <FiEdit2 aria-hidden="true" />
                        </button>
                        {confirmDeleteId === order.id ? (
                          <button
                            type="button"
                            className="btn btn--danger btn--sm"
                            disabled={isBusy}
                            onClick={() => void remove(order.id)}
                          >
                            {t('Confirm')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="po-icon-btn po-icon-btn--danger"
                            aria-label={t('Delete {name}', { name: order.title })}
                            disabled={isBusy}
                            onClick={() => setConfirmDeleteId(order.id)}
                          >
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
